import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import EverTrue from 'app';

import { CachedContactsProvider } from '@evertrue/vms-insights';
import { fetchContactsById } from 'apps/volunteers/vms-queries';
import { GlobalDrawerProvider } from 'components/drawer/context/global-drawer-context';
import { GlobalContactSearchProvider } from 'apps/layout/controllers/global-contact-search-context';
import { FiltersDataProvider } from 'apps/query-builder/contexts/filters-data-context';
import { ListDataProvider } from 'apps/layout/controllers/list-data-context';
import { GlobalPortfolioProvider } from 'apps/portfolio-performance/contexts/global-portfolio-context';
import { GlobalJourneyProvider } from 'apps/journeys/context/global-journey-context';
import { TasksNotificationProvider } from 'apps/tasks/hooks/tasks-notification-context';
import { ContactModalProvider } from 'apps/profile/contexts/contact-modal-context';
import { LDProvider } from 'launchdarkly-react-client-sdk';
import LaunchDarklySync from 'base/launch-darkly/launch-darkly-sync';
import SiteAccessRedirect from 'base/launch-darkly/site-access-redirect';
import { GlobalOutreachProvider } from 'apps/outreach/contexts/global-outreach-context';
import { GlobalSearchProvider } from 'apps/layout/global-search/global-search-context';
import { EmailDraftsProvider } from 'apps/outreach/email/drafts/email-drafts-context';
import { ScheduledEmailProvider } from 'apps/outreach/email/scheduled/scheduled-email-context';
import { EmailThreadsProvider } from 'apps/outreach/email/threads/email-threads-context';
import { OutreachFilterProvider } from 'apps/outreach/email/contexts/outreach-filter-context';

// Make sure this state really needs to be global before adding another Provider!
const GlobalProviderController = ({ user = {}, ...props }) => {
  // app-startup-routine.js sets a preliminary org id early in boot (so the
  // router doesn't bounce to /login/org) then re-scopes to the real oid
  // asynchronously. This component only re-renders on User/Session store
  // changes, so without this it can permanently mount LDProvider against
  // the preliminary oid — org:initialized is legacy's existing signal for
  // "org data just settled" (see components/gated.coffee), re-used here to
  // force a re-render once the real oid is known.
  const [, forceUpdate] = useState(0);
  useEffect(() => {
    const onOrgInitialized = () => forceUpdate((tick) => tick + 1);
    EverTrue.vent.on('org:initialized', onOrgInitialized);
    // Setup.enterApp can emit org:initialized synchronously right after
    // mount (OrgSource.set -> setOrgData), before this listener attaches —
    // reconcile once immediately so an already-fired event still picks up
    // the current OID instead of being missed if scoping never fires again.
    onOrgInitialized();
    return () => {
      EverTrue.vent.off('org:initialized', onOrgInitialized);
    };
  }, []);

  const OID = EverTrue.store.org.get('id');

  const organizationContext = {
    kind: 'organization',
    key: OID,
  };

  const userContext = {
    kind: 'user',
    key: user.id,
    name: user.name,
    email: user.email,
  };

  const platformContext = {
    kind: 'platform',
    key: 'web',
  };

  const multiContext = {
    kind: 'multi',
    user: userContext,
    organization: organizationContext,
    platform: platformContext,
  };

  return (
    <LDProvider
      clientSideID={EverTrue.config.launch_darkly_token} // change for stage / prod
      context={multiContext}
      // LDProvider only re-initializes its client on mount (see
      // launchdarkly-react-client-sdk's provider.tsx componentDidUpdate) —
      // a context prop change alone never re-evaluates flags. Keying on the
      // ids forces a full remount once org:initialized corrects a
      // preliminary oid, or on impersonation/user change.
      key={`${user.id}-${OID}`}
      // https://launchdarkly.github.io/js-client-sdk/interfaces/LDOptions.html
    >
      <SiteAccessRedirect>
        <LaunchDarklySync>
          <CachedContactsProvider fetchContactsById={fetchContactsById}>
            <GlobalSearchProvider>
              <GlobalOutreachProvider>
                <GlobalDrawerProvider>
                  <GlobalJourneyProvider>
                    <GlobalContactSearchProvider>
                      <GlobalPortfolioProvider>
                        <ListDataProvider>
                          <FiltersDataProvider>
                            <ContactModalProvider>
                              <OutreachFilterProvider>
                                <EmailDraftsProvider>
                                  <ScheduledEmailProvider>
                                    <EmailThreadsProvider>
                                      <TasksNotificationProvider>{props.children}</TasksNotificationProvider>
                                    </EmailThreadsProvider>
                                  </ScheduledEmailProvider>
                                </EmailDraftsProvider>
                              </OutreachFilterProvider>
                            </ContactModalProvider>
                          </FiltersDataProvider>
                        </ListDataProvider>
                      </GlobalPortfolioProvider>
                    </GlobalContactSearchProvider>
                  </GlobalJourneyProvider>
                </GlobalDrawerProvider>
              </GlobalOutreachProvider>
            </GlobalSearchProvider>
          </CachedContactsProvider>
        </LaunchDarklySync>
      </SiteAccessRedirect>
    </LDProvider>
  );
};

GlobalProviderController.propTypes = {
  user: PropTypes.object,
  children: PropTypes.any,
};

export default GlobalProviderController;
